import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { Delete, Edit, Add } from '@mui/icons-material';
import type { ConfigProfileDto } from '../api/configProfiles';
import { listConfigProfiles, upsertConfigProfile, deleteConfigProfile, testConfigConnectivity } from '../api/configProfiles';
import { useTranslation } from 'react-i18next';

const emptyProfile: ConfigProfileDto = {
  name: '',
  externalServiceUrl: '',
  nextcloudUrl: '',
  apinformesUrl: '',
  isDefault: false,
};

const ConfigProfilesPage: React.FC = () => {
  const [profiles, setProfiles] = useState<ConfigProfileDto[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [open, setOpen] = useState<boolean>(false);
  const [form, setForm] = useState<ConfigProfileDto>({ ...emptyProfile });
  const [testing, setTesting] = useState<boolean>(false);
  const [testResult, setTestResult] = useState<string>('');
  const [confirmDelete, setConfirmDelete] = useState<null | { name: string }>(null);
  const [errors, setErrors] = useState<{ name?: string; nextcloudUrl?: string; externalServiceUrl?: string; apinformesUrl?: string }>({});
  const { t } = useTranslation();

  const load = async () => {
    setLoading(true);
    try {
      const list = await listConfigProfiles();
      setProfiles(list);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleOpenCreate = () => {
    setForm({ ...emptyProfile });
    setOpen(true);
  };

  const handleEdit = (p: ConfigProfileDto) => {
    setForm({ ...p });
    setOpen(true);
  };

  const handleSave = async () => {
    const urlRegex = /^(https?:)\/\//i;
    const nextErrors: typeof errors = {};
    if (!form.name) nextErrors.name = t('common.nameRequired', 'El nombre es obligatorio');
    if (form.nextcloudUrl && !urlRegex.test(form.nextcloudUrl)) nextErrors.nextcloudUrl = t('common.invalidUrl', 'URL no válida');
    if (form.externalServiceUrl && !urlRegex.test(form.externalServiceUrl)) nextErrors.externalServiceUrl = t('common.invalidUrl', 'URL no válida');
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;
    await upsertConfigProfile(form);
    setOpen(false);
    await load();
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    await deleteConfigProfile(confirmDelete.name);
    setConfirmDelete(null);
    await load();
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult('');
    try {
      const res = await testConfigConnectivity({
        nextcloudUrl: form.nextcloudUrl || undefined,
        externalServiceUrl: form.externalServiceUrl || undefined,
        apinformesUrl: form.apinformesUrl || undefined,
      });
      const parts: string[] = [];
      if (res.nextcloud) parts.push(`Nextcloud: ${res.nextcloud.ok ? 'OK' : 'Fallo'}${res.nextcloud.status ? ` (${res.nextcloud.status})` : ''}${res.nextcloud.error ? ` - ${res.nextcloud.error}` : ''}`);
      if (res.external) parts.push(`Servicio externo: ${res.external.ok ? 'OK' : 'Fallo'}${res.external.status ? ` (${res.external.status})` : ''}${res.external.error ? ` - ${res.external.error}` : ''}`);
      if (res.apinformes) parts.push(`Servicio informes: ${res.apinformes.ok ? 'OK' : 'Fallo'}${res.apinformes.status ? ` (${res.apinformes.status})` : ''}${res.apinformes.error ? ` - ${res.apinformes.error}` : ''}`);
      setTestResult(parts.join(' | '));
    } catch (e: any) {
      setTestResult(String(e?.message || e));
    } finally {
      setTesting(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5">{t('profiles.title', 'Entornos')}</Typography>
        <Button startIcon={<Add />} variant="contained" onClick={handleOpenCreate}>{t('profiles.new', 'Nuevo entorno')}</Button>
      </Box>

      {loading ? (
        <Typography>{t('common.loading', 'Cargando...')}</Typography>
      ) : (
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr', lg: '1fr 1fr 1fr' },
          gap: 2,
        }}>
          {profiles.map((p) => (
            <Box key={p.name} sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
              <Typography variant="h6" sx={{ mb: 1 }}>{p.name}</Typography>
              <Typography variant="body2" sx={{ mb: 0.5 }}>{t('profiles.nextcloud', 'Nextcloud')}: {p.nextcloudUrl || '-'}</Typography>
              <Typography variant="body2" sx={{ mb: 0.5 }}>{t('profiles.externalService', 'Servicio externo')}: {p.externalServiceUrl || '-'}</Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>{t('profiles.reportsService', 'Servicio informes')}: {p.apinformesUrl || '-'}</Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button startIcon={<Edit />} size="small" onClick={() => handleEdit(p)}>{t('common.edit', 'Editar')}</Button>
                <Button color="error" startIcon={<Delete />} size="small" onClick={() => setConfirmDelete({ name: p.name })}>{t('common.delete', 'Eliminar')}</Button>
              </Box>
            </Box>
          ))}
        </Box>
      )}

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{form.id ? t('profiles.edit', 'Editar entorno') : t('profiles.new', 'Nuevo entorno')}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField label={t('common.name', 'Nombre')} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} fullWidth required error={!!errors.name} helperText={errors.name} />
            <TextField label={t('profiles.nextcloudUrl', 'Nextcloud URL')} placeholder="http(s)://..." value={form.nextcloudUrl || ''} onChange={(e) => setForm({ ...form, nextcloudUrl: e.target.value })} fullWidth error={!!errors.nextcloudUrl} helperText={errors.nextcloudUrl} />
            <TextField label={t('profiles.externalServiceUrl', 'Servicio externo URL')} placeholder="http(s)://..." value={form.externalServiceUrl || ''} onChange={(e) => setForm({ ...form, externalServiceUrl: e.target.value })} fullWidth error={!!errors.externalServiceUrl} helperText={errors.externalServiceUrl} />
            <TextField label={t('profiles.reportsServiceUrl', 'Servicio informes URL')} placeholder="http(s)://..." value={form.apinformesUrl || ''} onChange={(e) => setForm({ ...form, apinformesUrl: e.target.value })} fullWidth error={!!errors.apinformesUrl} helperText={errors.apinformesUrl} />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Switch checked={!!form.isDefault} onChange={(e) => setForm({ ...form, isDefault: e.target.checked })} />
              <Typography>{t('profiles.markDefault', 'Marcar como predeterminado')}</Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
              <Button variant="outlined" onClick={handleTest} disabled={testing}>{t('profiles.testConnectivity', 'Test Conectividad')}</Button>
              <Typography variant="body2" color={testResult.includes('Fallo') ? 'error' : 'text.secondary'}>{testResult}</Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>{t('common.cancel', 'Cancelar')}</Button>
          <Button variant="contained" onClick={handleSave}>{t('common.save', 'Guardar')}</Button>
        </DialogActions>
      </Dialog>

      {/* Confirmación de borrado */}
      <Dialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)}>
        <DialogTitle>{t('profiles.delete', 'Eliminar entorno')}</DialogTitle>
        <DialogContent>
          <Typography>{t('profiles.confirmDelete', '¿Seguro que deseas eliminar el entorno')} "{confirmDelete?.name}"?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)}>{t('common.cancel', 'Cancelar')}</Button>
          <Button color="error" variant="contained" onClick={handleDelete}>{t('common.delete', 'Eliminar')}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ConfigProfilesPage;


